(() => {
  if (window.magpieCatalogueScroll) return;
  const controllers = new Map();
  const cardSelector = ':scope > .product-block';
  const keyFor = (card) => card.dataset.productId || card.querySelector('a[href*="/products/"]')?.pathname || card.querySelector('a[href]')?.pathname;
  const nextURL = (value, base = location.href) => {
    if (!value) return null;
    const url = new URL(value, base);
    if (url.origin !== location.origin || url.pathname !== location.pathname) throw new Error('Invalid catalogue URL');
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    return url;
  };

  class CatalogueScroll {
    constructor(section, grid, pagination, filterFailed = false) {
      this.section = section;
      this.grid = grid;
      this.pagination = pagination;
      this.sectionId = section.id.replace('shopify-section-', '');
      this.next = nextURL(pagination.dataset.nextUrl);
      this.page = Number(pagination.dataset.page);
      this.seen = new Set([...grid.querySelectorAll(cardSelector)].map(keyFor));
      this.visited = new Set();
      this.disposed = false;
      this.retryCount = 0;
      this.status = document.createElement('div');
      this.status.className = 'magpie-scroll-status';
      this.message = document.createElement('span');
      this.message.setAttribute('role', 'status');
      this.message.setAttribute('aria-live', 'polite');
      this.retry = document.createElement('button');
      this.retry.type = 'button';
      this.retry.className = 'btn btn--secondary';
      this.retry.textContent = pagination.dataset.retry;
      this.retry.hidden = true;
      this.retry.addEventListener('click', () => {
        if (this.filterFailed) { location.reload(); return; }
        this.retryCount = 0;
        this.state('idle');
        this.prefetch();
      });
      this.status.append(this.message, this.retry);
      pagination.after(this.status);
      pagination.classList.add('magpie-scroll-pagination');
      this.onScroll = () => {
        if (this.frame || this.disposed) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.check(); });
      };
      this.onOnline = () => {
        if (this.phase === 'error' && !this.filterFailed) {
          this.retryCount = 0;
          this.state('idle');
          this.prefetch();
        }
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      window.addEventListener('online', this.onOnline);
      this.observer = new IntersectionObserver(() => this.check(), { rootMargin: `${Math.max(1600, innerHeight * 3)}px 0px` });
      this.observer.observe(this.status);
      this.state(this.next ? 'idle' : 'complete');
      if (filterFailed) { this.filterFailed = true; this.state('error'); }
      else this.prefetch();
    }

    state(phase) {
      this.phase = phase;
      this.grid.dataset.magpieScrollState = phase;
      this.grid.setAttribute('aria-busy', phase === 'loading' ? 'true' : 'false');
      this.retry.hidden = phase !== 'error';
      this.status.dataset.state = phase;
      this.message.textContent = phase === 'error' ? this.pagination.dataset.error : phase === 'complete' ? this.pagination.dataset.complete : phase === 'loading' ? this.pagination.dataset.loading : '';
    }

    nearEnd() {
      return this.status.getBoundingClientRect().top < innerHeight + Math.max(1600, innerHeight * 3);
    }

    check() {
      if (this.disposed || !this.grid.isConnected || this.phase === 'paused' || this.phase === 'error' || this.phase === 'complete') return;
      if (!this.nearEnd()) return;
      if (this.buffer) this.append();
      else if (this.next) { this.prefetch(); this.state('loading'); }
    }

    async prefetch() {
      if (this.disposed || !this.next || this.request || this.buffer || this.phase === 'error' || this.phase === 'paused') return;
      const target = this.next.href;
      if (this.visited.has(target)) { this.state('error'); return; }
      const controller = new AbortController();
      this.request = controller;
      if (this.phase !== 'loading') this.state('prefetching');
      const timeout = setTimeout(() => controller.abort(), 10000);
      const started = performance.now();
      try {
        const url = new URL(target);
        url.searchParams.set('section_id', this.sectionId);
        const response = await fetch(url.href, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`Catalogue HTTP ${response.status}`);
        const html = await response.text();
        if (this.disposed) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = doc.querySelector('filter-container .product-grid');
        const meta = doc.querySelector('[data-magpie-pagination]');
        const incomingPage = Number(meta?.dataset.page);
        const expectedPage = Number(new URL(target).searchParams.get('page') || 1);
        if (!nextGrid || !meta || incomingPage !== expectedPage || incomingPage <= this.page) throw new Error('Invalid catalogue page');
        const cards = [...nextGrid.querySelectorAll(cardSelector)];
        if (!cards.length || !cards.some(card => !this.seen.has(keyFor(card)))) throw new Error('Catalogue did not advance');
        const next = nextURL(meta.dataset.nextUrl, target);
        if (next && (next.href === target || Number(next.searchParams.get('page')) <= incomingPage)) throw new Error('Repeated catalogue page');
        this.buffer = { cards, next, page: incomingPage, target };
        this.grid.dataset.magpiePrefetchMs = String(Math.round(performance.now() - started));
        this.warmImages(cards);
        this.retryCount = 0;
        this.state('ready');
      } catch (error) {
        if (this.disposed) return;
        this.retryCount += 1;
        if (this.retryCount < 2) {
          this.state('prefetching');
          this.retryTimer = setTimeout(() => { this.retryTimer = null; this.prefetch(); }, 500);
        } else this.state('error');
      } finally {
        clearTimeout(timeout);
        if (this.request === controller) this.request = null;
      }
      if (!this.disposed && this.buffer) this.check();
    }

    warmImages(cards) {
      const columns = getComputedStyle(this.grid).gridTemplateColumns.split(' ').length;
      this.warmed = cards.slice(0, Math.max(2, columns)).map(card => {
        const source = card.querySelector('img');
        if (!source) return null;
        const image = new Image();
        image.decoding = 'async';
        image.sizes = `${Math.ceil(this.grid.clientWidth / Math.max(1, columns))}px`;
        if (source.srcset) image.srcset = source.srcset;
        image.src = source.src;
        return image;
      });
    }

    append() {
      const batch = this.buffer;
      if (!batch || this.disposed) return;
      this.buffer = null;
      this.visited.add(batch.target);
      const fragment = document.createDocumentFragment();
      let added = 0;
      batch.cards.forEach(card => {
        const key = keyFor(card);
        if (this.seen.has(key)) return;
        this.seen.add(key);
        card.removeAttribute('data-cc-animate');
        card.querySelectorAll('[data-cc-animate]').forEach(el => el.removeAttribute('data-cc-animate'));
        card.querySelectorAll('img').forEach(image => { image.loading = 'lazy'; image.decoding = 'async'; image.setAttribute('fetchpriority', 'low'); });
        fragment.appendChild(card);
        added += 1;
      });
      this.grid.appendChild(fragment);
      this.page = batch.page;
      this.next = batch.next;
      this.pagination.dataset.page = String(this.page);
      this.pagination.dataset.nextUrl = this.next?.href || '';
      this.grid.dataset.magpieLoaded = String(this.seen.size);
      this.state(this.next ? 'idle' : 'complete');
      this.grid.dispatchEvent(new CustomEvent('magpie:catalogue:appended', { bubbles: true, detail: { added, page: this.page } }));
      this.prefetch();
    }

    dispose() {
      this.disposed = true;
      this.request?.abort();
      clearTimeout(this.retryTimer);
      cancelAnimationFrame(this.frame);
      this.observer.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      window.removeEventListener('online', this.onOnline);
      this.status.remove();
      this.pagination.classList.remove('magpie-scroll-pagination');
      this.grid.removeAttribute('aria-busy');
    }
  }

  const mount = (section, filterFailed = false) => {
    const grid = section.querySelector('filter-container .product-grid');
    const pagination = section.querySelector('[data-magpie-pagination]');
    if (!grid || !pagination || grid.closest('carousel-slider, .magpie-landing-products--carousel') || grid.classList.contains('product-grid--carousel')) return;
    const existing = controllers.get(section);
    if (existing && existing.grid === grid && existing.pagination === pagination && !existing.disposed) return;
    existing?.dispose();
    try { controllers.set(section, new CatalogueScroll(section, grid, pagination, filterFailed)); } catch (error) { pagination.classList.remove('magpie-scroll-pagination'); }
  };
  const start = () => {
    document.querySelectorAll('.shopify-section:has(filter-container)').forEach(section => mount(section));
  };
  window.magpieCatalogueScroll = true;
  document.addEventListener('magpie:catalogue:before', event => {
    const section = event.target.closest('.shopify-section');
    const current = controllers.get(section);
    current?.dispose();
    controllers.delete(section);
  });
  document.addEventListener('magpie:catalogue:updated', event => mount(event.target.closest('.shopify-section')));
  document.addEventListener('magpie:catalogue:error', event => {
    const section = event.target.closest('.shopify-section');
    mount(section, true);
  });
  document.addEventListener('shopify:section:load', start);
  document.addEventListener('shopify:section:unload', event => {
    controllers.get(event.target)?.dispose(); controllers.delete(event.target);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
